import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Runs after a user signs in:
 *  1. Looks up their public IP (ipify) and upserts it into user_ip_log.
 *  2. Checks if that IP is on banned_ips → signs them out.
 *  3. Checks profile.force_signout_at vs session start → signs them out if kicked.
 */
export function useModerationGuard() {
  const { user, session, signOut } = useAuth();

  useEffect(() => {
    if (!user || !session) return;
    let cancelled = false;

    (async () => {
      try {
        // 1. Fetch IP
        let ip: string | null = null;
        try {
          const res = await fetch("https://api.ipify.org?format=json");
          const j = await res.json();
          ip = j?.ip || null;
        } catch {
          /* ignore */
        }

        if (cancelled) return;

        // 2. IP ban check
        if (ip) {
          const { data: banned } = await supabase.rpc("is_ip_banned", { _ip: ip });
          if (banned === true) {
            toast.error("Your IP address has been banned from this platform.");
            await signOut();
            return;
          }

          // Log IP (upsert)
          await supabase
            .from("user_ip_log")
            .upsert(
              { user_id: user.id, ip_address: ip, last_seen: new Date().toISOString() },
              { onConflict: "user_id,ip_address" },
            );
        }

        // 3. Force-signout (kick) check
        const sessionStartIso = session.user?.last_sign_in_at;
        const { data: profile } = await supabase
          .from("profiles")
          .select("force_signout_at")
          .eq("user_id", user.id)
          .maybeSingle();
        if (profile?.force_signout_at && sessionStartIso) {
          if (new Date(profile.force_signout_at) > new Date(sessionStartIso)) {
            toast.error("You have been signed out by an administrator.");
            await signOut();
          }
        }
      } catch (e) {
        console.warn("Moderation guard failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, session?.access_token]);
}
